import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosBaseURL from "..";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";

/**
 * Bank master CRUD client. Backed by `BankController` at
 * `/api/v1/admin/banks` on the dagri service. Auth uses the same
 * `${userId}/${token}` `header` value the rest of the portal sends.
 */

export interface Bank {
  id: number;
  bankName: string;
  code?: string | null;
  swiftCode?: string | null;
  country?: string | null;
  isActive: boolean;
}

export interface BankPayload {
  bankName: string;
  code?: string | null;
  swiftCode?: string | null;
  country?: string | null;
  isActive?: boolean;
}

const BANKS_KEY = "banks";

export const useGetBanks = (options?: { activeOnly?: boolean }) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: [BANKS_KEY, options?.activeOnly ?? false],
    enabled: !!user?.token,
    staleTime: 60_000,
    queryFn: async (): Promise<Bank[]> => {
      const params = new URLSearchParams();
      if (options?.activeOnly) params.append("activeOnly", "true");
      const url = `/v1/admin/banks${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const { data } = await axiosBaseURL.get<Bank[]>(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });
      return data ?? [];
    },
  });
};

export const useGetBankById = (id?: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: [BANKS_KEY, "byId", id],
    enabled: !!user?.token && !!id,
    queryFn: async (): Promise<Bank | null> => {
      const { data } = await axiosBaseURL.get<Bank>(`/v1/admin/banks/${id}`, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });
      return data ?? null;
    },
  });
};

export const useCreateBank = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BankPayload): Promise<Bank> => {
      const { data } = await axiosBaseURL.post<Bank>(
        `/v1/admin/banks`,
        payload,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BANKS_KEY] });
    },
  });
};

export const useUpdateBank = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      payload: BankPayload;
    }): Promise<Bank> => {
      const { data } = await axiosBaseURL.put<Bank>(
        `/v1/admin/banks/${args.id}`,
        args.payload,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BANKS_KEY] });
    },
  });
};

export const useActivateBank = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Bank> => {
      const { data } = await axiosBaseURL.post<Bank>(
        `/v1/admin/banks/${id}/activate`,
        {},
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BANKS_KEY] });
    },
  });
};

export const useDeactivateBank = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Bank> => {
      const { data } = await axiosBaseURL.post<Bank>(
        `/v1/admin/banks/${id}/deactivate`,
        {},
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BANKS_KEY] });
    },
  });
};
